/**
 * @fileoverview Notifications Page
 * @theme Polished Corporate - Teal accents, refined shadows
 */

import { useState } from 'react';
import { Button } from '@components/ui/button';
import { useNotifications } from '@contexts/NotificationContext';
import { useMarket } from '@contexts/MarketContext';

const notificationStyles: Record<string, { bg: string; iconBg: string; icon: JSX.Element }> = {
  warning: {
    bg: 'border-l-amber-500',
    iconBg: 'bg-amber-50 text-amber-600',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
  },
  success: {
    bg: 'border-l-emerald-500',
    iconBg: 'bg-emerald-50 text-emerald-600',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  info: {
    bg: 'border-l-teal-500',
    iconBg: 'bg-teal-50 text-teal-600',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  error: {
    bg: 'border-l-red-500',
    iconBg: 'bg-red-50 text-red-600',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
};

export function NotificationsPage(): JSX.Element {
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification, getLocalizedNotification } = useNotifications();
  const { isBrazil } = useMarket();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const filteredNotifications = filter === 'unread'
    ? notifications.filter((n) => !n.read)
    : notifications;

  return (
    <div className="min-h-screen bg-gray-50/30">
      <div className="container py-6">
        <div className="mx-auto max-w-3xl">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-gray-900">
                  {isBrazil ? 'Notificações' : 'Notifications'}
                </h2>
                {unreadCount > 0 && (
                  <span className="rounded-full bg-teal-100 px-2.5 py-0.5 text-sm font-medium text-teal-700">
                    {unreadCount} {isBrazil ? 'novas' : 'new'}
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-500">
                {isBrazil ? 'Mantenha-se atualizado sobre sua atividade financeira' : 'Stay updated on your financial activity'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={markAllAsRead}
                disabled={unreadCount === 0}
                className="rounded-xl border-gray-200"
              >
                {isBrazil ? 'Marcar todas como lidas' : 'Mark all as read'}
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-4 flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`rounded-xl px-4 py-2 text-sm font-medium transition-all ${
                filter === 'all'
                  ? 'bg-teal-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50 shadow-sm'
              }`}
            >
              {isBrazil ? 'Todas' : 'All'} ({notifications.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`rounded-xl px-4 py-2 text-sm font-medium transition-all ${
                filter === 'unread'
                  ? 'bg-teal-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50 shadow-sm'
              }`}
            >
              {isBrazil ? 'Não lidas' : 'Unread'} ({unreadCount})
            </button>
          </div>

          {/* Notifications List */}
          <div className="space-y-3">
            {filteredNotifications.length === 0 ? (
              <div className="rounded-xl bg-white p-8 text-center shadow-card">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <p className="mt-4 font-medium text-gray-900">
                  {isBrazil ? 'Nenhuma notificação' : 'No notifications'}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {isBrazil ? 'Você está em dia!' : "You're all caught up!"}
                </p>
              </div>
            ) : (
              filteredNotifications.map((notification) => {
                const localized = getLocalizedNotification(notification);
                return (
                  <div
                    key={notification.id}
                    className={`rounded-xl bg-white shadow-card overflow-hidden border-l-4 ${
                      notificationStyles[notification.type]?.bg
                    } ${!notification.read ? 'ring-1 ring-teal-200' : ''}`}
                  >
                    <div className="p-4">
                      <div className="flex items-start gap-4">
                        <div className={`rounded-xl p-2.5 ${notificationStyles[notification.type]?.iconBg}`}>
                          {notificationStyles[notification.type]?.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className={`font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                                {localized.title}
                              </p>
                              <p className="mt-1 text-sm text-gray-500">{localized.message}</p>
                              <p className="mt-2 text-xs text-gray-400">{localized.time}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {!notification.read && (
                                <button
                                  onClick={() => markAsRead(notification.id)}
                                  className="rounded-lg p-1.5 text-gray-400 hover:bg-teal-50 hover:text-teal-600 transition-colors"
                                  title={isBrazil ? 'Marcar como lida' : 'Mark as read'}
                                >
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </button>
                              )}
                              <button
                                onClick={() => deleteNotification(notification.id)}
                                className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                                title={isBrazil ? 'Excluir' : 'Delete'}
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
